import type { ChangeEvent, FunctionComponent } from "react";
import { useCallback } from "react";
import { FormControl } from "react-bootstrap";
import { formatForDisplay, sanitizeInput } from "../core/formatters";
import type { MaskedDataFieldProps } from "../core/types";
import { useFieldValidation } from "../useFieldValidation";

type InputProps = Pick<
	MaskedDataFieldProps,
	"value" | "type" | "onChange" | "validation" | "required" | "disabled"
>;

const MaskedDataFieldInput: FunctionComponent<InputProps> = ({
	value,
	type,
	onChange,
	validation,
	required = false,
	disabled = false,
}) => {
	const { isFocused, isInvalid, errorId, error, handleFocus, handleBlur } =
		useFieldValidation({ value, required, validation });

	const displayValue = isFocused ? value : formatForDisplay(value, type);

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const raw = sanitizeInput(e.target.value, type);
			if (onChange) {
				onChange(raw);
			}
		},
		[type, onChange],
	);

	return (
		<div className="MaskedDataField-input">
			<FormControl
				type="text"
				value={displayValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				disabled={disabled}
				required={required}
				aria-required={required}
				aria-invalid={isInvalid}
				aria-describedby={errorId}
				aria-label={`${type} input`}
				className={isInvalid ? "MaskedDataField-error" : ""}
			/>
			{isInvalid && (
				<div id={errorId} className="MaskedDataField-error-text" role="alert">
					{error}
				</div>
			)}
		</div>
	);
};

export default MaskedDataFieldInput;
